import { type ID } from '@funnellab/shared/src/dtos/base';
import { type OrganizationRole } from '@funnellab/shared/src/types/directus-enums.ts';
import { directusClient } from '@src/api/DirectusClient.ts';
import type { RecruiterDTO } from '@funnellab/shared/src/types/recruiters';

export type AcceptedRecruiter = {
  email: string;
  id: string;
  role: `${OrganizationRole}`;
  firstName: string | null;
  lastName: string | null;
  avatar: ID | null;
  accepted: true;
};

export type InvitedRecruiter = {
  email: string;
  id: string;
  expiresAt: Date;
  accepted: false;
};

export type Recruiter = AcceptedRecruiter | InvitedRecruiter;

const dtoToRecruiter = (recruiter: RecruiterDTO): Recruiter => {
  if (recruiter.accepted) {
    return {
      email: recruiter.email,
      id: recruiter.id,
      role: recruiter.role,
      accepted: true,
      firstName: recruiter.first_name,
      lastName: recruiter.last_name,
      avatar: recruiter.avatar,
    };
  } else {
    return {
      email: recruiter.email,
      id: recruiter.id,
      accepted: false,
      expiresAt: new Date(recruiter.expiresAt),
    };
  }
};

export async function getRecruitersWithInvited(
  organizationId: string,
): Promise<Recruiter[]> {
  const recruiters =
    await directusClient.getRecruitersByOrganization(organizationId);
  return recruiters.map(dtoToRecruiter);
}

export class RecruiterService {
  async addRecruiter(
    organizationId: string,
    contactEmail: string,
  ): Promise<Recruiter> {
    const newRecruiter = await directusClient.addRecruiter(
      organizationId,
      contactEmail,
    );
    return dtoToRecruiter(newRecruiter);
  }

  async removeRecruiter(organizationId: string, recruiterId: string) {
    return directusClient.removeRecruiter(organizationId, recruiterId);
  }

  async updateRecruiterRole(
    organizationId: string,
    contactId: string,
    role: `${OrganizationRole}`,
  ) {
    return directusClient.updateRecruiterRole(organizationId, contactId, role);
  }

  removeInvitedRecruiter(organizationId: string, recruiterId: string) {
    return directusClient.removeInvitedRecruiter(organizationId, recruiterId);
  }

  resendInvite(organizationId: string, recruiterId: string) {
    return directusClient.resendInvite(organizationId, recruiterId);
  }
}

const recruiterService = new RecruiterService();
export default recruiterService;
